import {Box, Button, Modal, Typography} from '@viptech/react-components'
import {addDays, differenceInDays} from 'date-fns'
import {useSnackbar} from 'notistack'
import {useMemo, useState} from 'react'
import {BtnClose} from 'src/components/BtnClose'
import {Input} from 'src/components/input/Input'
import useCreateApiKey from 'src/hooks/api/api-keys/useCreateApiKey'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'

type Props = {
  isOpen: boolean
  closeModal: () => void
  userIdSelected: number
  userName: string
}

export function CreateApiKeyModal({isOpen, closeModal, userIdSelected, userName}: Props) {
  const {mutateAsync: createApiKey} = useCreateApiKey()
  const {enqueueSnackbar} = useSnackbar()

  const [plainKey, setPlainKey] = useState<string | undefined>()
  const [name, setName] = useState<string>('')
  const [expiresIn, setExpiresIn] = useState<string | undefined>()

  const minDate = useMemo(() => {
    const today = addDays(new Date(), 1)
    const offset = today.getTimezoneOffset()
    today.setMinutes(today.getMinutes() - offset)
    return today.toISOString().split('T')[0]
  }, [])

  const handleSave = async () => {
    if (plainKey) return closeModal()

    try {
      const today = new Date().setHours(23, 59, 0, 0)
      const days = expiresIn ? differenceInDays(new Date(expiresIn).setHours(23, 59, 0, 0), today) + 1 : undefined
      const response = await createApiKey({userId: userIdSelected, name, expiresInDays: days})
      setPlainKey(response.data.data.plainKey)
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Não foi possível criar a api key no momento')
    }
  }

  const copieKey = () => {
    if (!plainKey) return
    navigator.clipboard.writeText(plainKey)
    enqueueSnackbar('Chave copiada com sucesso', {variant: 'success'})
  }

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} backgroundColor='white' width='650px'>
      <Box display='flex' direction='column' padding='20px' rowGap='35px'>
        <BtnClose onClose={closeModal} />
        {!plainKey ? (
          <>
            <Box display='flex' direction='column'>
              <Typography variant='p' weight={500} size='16px' children={'Criar api-key'}></Typography>
              <Typography variant='span' size='12px' color='#8E8E8E'>
                Para utilizar nossa api crie uma chave para o usuário {userName}
              </Typography>
            </Box>

            <Box display='flex' direction='column' rowGap='15px'>
              <Input.Root
                label='Nome da chave'
                labelFontSize='14px'
                borderColor='#D0D5DD'
                fontSize='12px'
                labelFontWeight='400'
                min={4}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={99}
                required
              />

              <Input.Root
                label='Data de expiração'
                type='date'
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
                labelFontSize='14px'
                borderColor='#D0D5DD'
                fontSize='12px'
                labelFontWeight='400'
                min={minDate}
              />
            </Box>
          </>
        ) : (
          <>
            <Box display='flex' direction='column'>
              <Typography variant='p' weight={500} size='16px' children={'Chave de acesso criada com sucesso!'}></Typography>
              <Typography variant='span' size='12px' color='#8E8E8E'>
                Copie e salve sua api-key abaixo. Mantenha-a segura e não compartilhe publicamente.
              </Typography>
            </Box>

            <Box padding='5px' rowGap='15px' display='flex' direction='column'>
              <Box display='flex' justifyContent='space-between' padding='10px' borderRadius='8px' border='1px solid #D0D5DD'>
                <Typography variant='p' size='12px' alignSelf='center'>
                  {plainKey}
                </Typography>

                <Button variant='contained' fontSize='12px' height='30px' onClick={copieKey}>
                  Copiar
                </Button>
              </Box>
              <Typography
                variant='p'
                size='14px'
                weight={600}
                color='#F79009'
                textAlign='center'
                style={{background: '#FFF3DE', borderRadius: '6px', padding: '10px 0'}}>
                Importante: Tenha certeza de salvar sua chave. Essa é a única vez que você a verá!
              </Typography>
            </Box>
          </>
        )}
        <Box display='flex' width='100%' columnGap='10px' justifyContent='flex-end'>
          {!plainKey && (
            <Button fontSize='12px' height='32px' width='120px' variant='outlined' color='#8E8E8E' onClick={closeModal}>
              Cancelar
            </Button>
          )}
          <Button
            fontSize='12px'
            height='32px'
            width='120px'
            disabledTextColor='#8E8E8E'
            variant={plainKey ? 'outlined' : 'contained'}
            onClick={handleSave}
            disabled={!name}>
            {plainKey ? 'Fechar' : 'Criar'}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
